import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Eye, X } from 'lucide-react';
import { toast } from 'sonner';
import { getPreviewSession, clearPreviewSession } from '../lib/previewSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABEL = {
  customer: 'Customer',
  vip: 'VIP',
  employee: 'Employee',
  technician: 'Technician',
  manager: 'Manager',
  service_manager: 'Service Manager',
  construction_manager: 'Construction Manager',
  office_staff: 'Office Staff',
  property_manager: 'Property Manager',
  asset_manager: 'Asset Manager',
  maintenance_supervisor: 'Maintenance Supervisor',
};

const PreviewBanner = () => {
  const [session, setSession] = useState(getPreviewSession());

  useEffect(() => {
    const onChange = () => setSession(getPreviewSession());
    window.addEventListener('nexflow:preview-change', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('nexflow:preview-change', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  if (!session) return null;

  const stop = async () => {
    try {
      // Logged on the server as the original admin (whitelisted in the read-only middleware)
      await axios.post(`${API}/admin/preview/stop`, {}, { withCredentials: true });
    } catch {
      /* non-fatal */
    }
    clearPreviewSession();
    toast.success('Exited preview — returning to admin');
    // Full reload so axios interceptor stops sending X-Preview-As-User-Id and route guards re-read admin role.
    window.location.href = '/admin/users';
  };

  const roleLabel = ROLE_LABEL[session.role] || (session.role || 'user');

  return (
    <div
      data-testid="preview-banner"
      className="sticky top-0 z-50 bg-[#1E3A8A] border-b-2 border-cyan-400 text-white px-4 py-2 flex flex-wrap items-center justify-between gap-3 shadow-lg"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Eye className="w-4 h-4 text-cyan-300 shrink-0" />
        <p className="text-xs sm:text-sm truncate">
          <span className="text-cyan-300 font-bold tracking-[0.2em] uppercase">Read-Only Preview</span>{' '}
          <span className="text-cyan-100/70 hidden sm:inline">·</span>{' '}
          <span className="text-white font-medium">Viewing portal as {session.name}</span>{' '}
          {session.context && <span className="text-white font-medium">for {session.context}</span>}{' '}
          <span className="text-cyan-100/60 text-[10px] uppercase tracking-wider ml-1">({roleLabel})</span>
          <span className="text-cyan-100/80 hidden md:inline ml-2">— Exit preview to make changes.</span>
        </p>
      </div>
      <button
        data-testid="preview-exit"
        onClick={stop}
        className="shrink-0 bg-white text-[#1E3A8A] hover:bg-cyan-50 text-xs font-semibold px-3 py-1.5 rounded-md flex items-center gap-1"
      >
        <X className="w-3 h-3" /> Return to Admin
      </button>
    </div>
  );
};

export default PreviewBanner;
